/**
 * The money pipeline's wire shapes: invoices, their lines, the payments against
 * them, and the record of a billing run.
 *
 * `PAYMENTS.md` §Data model is the source for `payment`; `SERVICES.md` §Monthly
 * invoice for what a monthly invoice carries; `ARCHITECTURE.md` §Money pipeline for
 * the two properties that are not obvious from either.
 *
 * Four things here carry a rule rather than a value.
 *
 *   **Settlement is derived, never stored.** `ARCHITECTURE.md` §Money pipeline:
 *   "Settlement is derived (`sum(payments) >= total`), computed in one query, never
 *   cached on the invoice." `InvoiceSchema.paid_at` is therefore *not* a cached
 *   settlement: it is the timestamp of the `paid` transition, written once by
 *   `settle()` and by nothing else. `paid_amount` beside it is the sum, computed at
 *   read time, which is what a partially paid invoice prints.
 *
 *   Session 44 adds the one other thing that settles an invoice: a **write-off**,
 *   money the studio decided not to collect. It is never a payment, so `paid_amount`
 *   still counts payments only, and `written_off` and `outstanding` sit beside it —
 *   `outstanding` is total − paid − written off, worked out on the server, so no page
 *   has to subtract.
 *
 *   **`subtotal` and `total` are equal and both are here.**
 *   `.docs/14-billing-invoices.md` §1: "no tax; equal — keep both so a tax line is a
 *   column later, not a migration of meaning". `.docs/00-index.md` §6, *Sales tax*:
 *   "None on invoices."
 *
 *   **A line's `amount` is `qty × unit_amount`, except a discount.**
 *   `ARCHITECTURE.md` §Invariants: `invoice_line.amount CHECK (= qty * unit_amount)
 *   for kind ≠ discount`. A discount is an amount that comes off rather than a thing
 *   bought, which is also why it is the one negative amount an invoice carries.
 *
 *   **A dry run has no reference.** Minting one inside a transaction that is about
 *   to be rolled back would leave a gap in a sequence `PAYMENTS.md` §Wise needs to be
 *   gapless, so `DryRunInvoiceSchema.ref` is null and says so.
 */
import { z } from 'zod';
import { invoiceMachine } from '../states.js';
import { CurrencySchema, DateOnlySchema, MoneySchema, PeriodSchema, Rfc3339Schema, } from './primitives.js';
import { ClientSlugSchema, InvoiceRefSchema } from './refs.js';
/* ---------------------------------------------------------------- the words */
/**
 * What an invoice is for.
 *
 * `SERVICES.md` §Billing models gives the first five: the monthly retainer invoice,
 * the one-off signup invoice that carries a prorated part-month beside the first
 * full one, a project's deposit, a project's milestone, and the final milestone
 * (whose invoice also carries the approved scope changes). `one_off` is the hand-made
 * one `invoices_draft$` writes, and it is the only invoice in this system with no run
 * and no work order behind it.
 */
export const INVOICE_KINDS = [
    'monthly',
    'signup',
    'milestone',
    'deposit',
    'final',
    'one_off',
];
export const InvoiceKindSchema = z
    .enum(INVOICE_KINDS)
    .describe('What the invoice is for: monthly (the retainer month), signup (the prorated part-month ' +
    'plus the first full one), deposit and milestone and final (a project), one_off (made ' +
    'by hand).');
/**
 * The kinds of line an invoice carries, in the order they print.
 *
 * `SERVICES.md` §Data model gives six and `.docs/14-billing-invoices.md` §1 adds
 * `scope_change`; the order here is the order `client/billing.html`'s fold shows —
 * the part-month first (it is the earliest thing on the page), then the retainer in
 * advance, then the extras and the scope changes in arrears, then usage, then
 * anything that comes off.
 *
 * `widget_fee` is session 46c's: one line per widget per month for a widget sold by the
 * month — qty 1 × the widget's own `monthly_amount`, in arrears, the widget's ref on
 * it. It prints straight after `usage` because it is about the same widgets and the
 * same month, and a client reading "what did my widgets cost in October" reads both.
 *
 * `INVOICE_LINE_KINDS` is therefore also the **sort key** for a invoice's lines, and
 * `seq` is assigned from it. Nothing else decides what order an invoice reads in.
 */
export const INVOICE_LINE_KINDS = [
    'proration',
    'retainer',
    'extra',
    'scope_change',
    'usage',
    'widget_fee',
    'milestone',
    'discount',
];
export const InvoiceLineKindSchema = z
    .enum(INVOICE_LINE_KINDS)
    .describe('What kind of line this is: proration, retainer, extra, scope_change, usage, ' +
    'widget_fee (a widget’s monthly fee, in arrears), milestone or discount. It also ' +
    'decides the order lines print in.');
/** Where an invoice's lines sit in the order they print. Never restated. */
export function lineKindOrder(kind) {
    return INVOICE_LINE_KINDS.indexOf(kind);
}
/**
 * `PAYMENTS.md` §Data model, verbatim: "source: stripe_ach | stripe_sepa |
 * stripe_card | wise_transfer | manual".
 */
export const PAYMENT_SOURCES = [
    'stripe_ach',
    'stripe_sepa',
    'stripe_card',
    'wise_transfer',
    'manual',
];
export const PaymentSourceSchema = z
    .enum(PAYMENT_SOURCES)
    .describe('Where the money came from: a Stripe debit or card, a Wise transfer, or manual — a ' +
    'cheque, a cash app, or a correction the studio recorded by hand.');
/**
 * `PAYMENTS.md` §Data model: a payment's own status, which is not the invoice's.
 *
 * `unmatched` is the review queue on `studio/money.html` — a Wise credit that landed
 * with no invoice reference and no unambiguous amount.
 *
 * Session 15 appends the two states a **human decision** leaves behind, and neither
 * is a gateway's word for anything.
 *
 *   `on_account`  money that is ours and is not any one invoice's yet. It is what a
 *                 surplus becomes when a credit covers two invoices and leaves
 *                 change ("Settle both, hold $645 on account" —
 *                 `studio/money.html`), and what an overpayment by card becomes when
 *                 a Wise transfer settled the invoice first. Deliberately **not** a
 *                 `credit_ledger` row: that ledger is prepaid widget credit, which is
 *                 a different promise (`.docs/15-billing-payments.md` §5).
 *   `parked`      a credit somebody looked at and could not place — "Park as
 *                 unidentified" on the same page. It leaves the queue without
 *                 pretending to be attributed.
 *   `allocated`   a credit that has been split. The row is still what the bank said
 *                 arrived — the amount is never rewritten — and the rows under it are
 *                 where the money went: one `succeeded` row per invoice, plus an
 *                 `on_account` row for anything left over. It is a state of its own
 *                 rather than `succeeded` because `succeeded` is counted, and
 *                 counting both a credit and the allocations under it would settle
 *                 every invoice twice.
 *
 * Only `succeeded` counts towards settlement, which is why adding three states here
 * changes no arithmetic anywhere (`queries.settledAmounts`).
 *
 * Session 47 appends one more, and it is the first that is **collected** without
 * being an invoice's:
 *
 *   `income`      money booked straight to a client account with no invoice, as the
 *                 studio's income — a Patreon payout arriving through Payoneer for an
 *                 account the studio owns, which has no invoices and never will. It
 *                 settles nothing (no invoice to settle) and is never spent by a later
 *                 match, which is what separates it from `on_account`; and it counts
 *                 in `formulae.md` §Collected beside `succeeded`, which is what
 *                 separates it from `parked`. A CHECK makes it carry a client.
 */
export const PAYMENT_STATUSES = [
    'pending',
    'succeeded',
    'failed',
    'unmatched',
    'refunded',
    'on_account',
    'parked',
    'allocated',
    'income',
];
export const PaymentStatusSchema = z
    .enum(PAYMENT_STATUSES)
    .describe('pending (in flight), succeeded (settled — only these count towards an invoice), failed, ' +
    'unmatched (money we cannot attribute yet), refunded, on_account (ours, not yet any ' +
    'one invoice’s), parked (looked at and set aside), allocated (a credit that has been ' +
    'split; the rows under it carry the money), income (booked to a client account with ' +
    'no invoice, as the studio’s income — counted as collected, settles nothing).');
/**
 * Whether a row is money arriving or money going back.
 *
 * `.docs/15-billing-payments.md` §5: "A refund is a **new** `payment` row with
 * `kind: refund`… a negative amount and `external_id` = the refund id, so settlement
 * stays `sum(payments)`."
 *
 * The **amount stays positive** and the kind carries the sign, which is the one
 * deviation from that sentence worth stating: `payment_amount_positive` has been a
 * CHECK since session 14 and `PAYMENTS.md` §Data model says corrections are new rows
 * rather than negative ones. `settledAmounts` subtracts a refund instead, so the
 * arithmetic is the same and no row in the table lies about which direction the
 * money went.
 */
export const PAYMENT_KINDS = ['payment', 'refund'];
export const PaymentKindSchema = z
    .enum(PAYMENT_KINDS)
    .describe('payment — money that arrived. refund — money given back, recorded as its own row so the ' +
    'original is never edited.');
/**
 * The rails a client can pay on. `PAYMENTS.md` §Rails, in its order of preference
 * for a large international invoice — "Wise, then ACH / SEPA, then card".
 *
 * BACS is deliberately absent: "not in scope — it requires a UK entity. UK clients
 * use card or Wise."
 *
 * Here rather than in `apps/api` because three things read it now: `client
 * .payment_rail_pref`, `invoice.rail_expected`, and the invoice mail's own `rail`
 * parameter. It was two lists in two files before this session.
 */
export const PAYMENT_RAILS = ['wise', 'ach', 'sepa', 'card'];
export const PaymentRailSchema = z
    .enum(PAYMENT_RAILS)
    .describe('How this invoice is expected to be paid: wise (they push the money), ach or sepa (we ' +
    'debit the account on file), card.');
/** `.docs/14-billing-invoices.md` §1: "`kind: monthly | signup`". */
export const BILLING_RUN_KINDS = ['monthly', 'signup'];
export const BillingRunKindSchema = z
    .enum(BILLING_RUN_KINDS)
    .describe('monthly — the ordinary run on the billing day. signup — the one-off run at signup, ' +
    'which also carries the prorated days of the signup month.');
/**
 * The run's own lifecycle. Not a state machine: there are no illegal transitions to
 * refuse, only four words for what happened, and `billing_run` is a log rather than
 * a thing anybody acts on.
 */
export const BILLING_RUN_STATUSES = ['running', 'done', 'failed'];
export const BillingRunStatusSchema = z
    .enum(BILLING_RUN_STATUSES)
    .describe('running, done, or failed with the error on the row.');
/**
 * The steps of the run, in the order `SERVICES.md` §Monthly billing run gives them,
 * with session 12's two applications as step zero.
 *
 * A resumed run reads this list off `billing_run.steps` and skips what is `done`;
 * `.docs/14-billing-invoices.md` §2: "Each step records its outputs in
 * `billing_run.steps`; a re-run reads them and skips what is done."
 */
export const BILLING_RUN_STEPS = [
    'period_end',
    'changes',
    'usage',
    'extras',
    'retainer',
    'invoice',
];
/** `.docs/14-billing-invoices.md` §1: "per step: `pending | done | skipped`". */
export const BILLING_RUN_STEP_STATES = ['pending', 'done', 'skipped'];
export const BillingRunStepSchema = z
    .object({
    state: z.enum(BILLING_RUN_STEP_STATES).describe('pending, done or skipped.'),
    /**
     * What the step produced, as ids or refs. A **list of strings**, because the one
     * thing a resumed run needs from a finished step is "which rows did you write",
     * and every one of those is addressable by an id or a reference.
     */
    outputs: z.array(z.string()).describe('The ids or references the step wrote. Empty if none.'),
    note: z.string().describe('One sentence about what happened. Empty when there is nothing.'),
})
    .describe('One step of the billing run, and what it left behind.');
/** The same, plus which step it is — how a run publishes its steps as a list. */
export const NamedBillingRunStepSchema = BillingRunStepSchema.extend({
    step: z.enum(BILLING_RUN_STEPS).describe('Which step this is.'),
}).describe('One step of the billing run, named.');
export const InvoiceStatusSchema = z
    .enum(invoiceMachine.states)
    .describe('draft → sent → processing → paid | failed (PAYMENTS.md). processing covers the days a ' +
    'bank debit is in flight; paid is set only by settlement.');
/* ---------------------------------------------------------------- the lines */
export const InvoiceLineSchema = z
    .object({
    seq: z.int().describe('Where the line prints, counting from 1.'),
    kind: InvoiceLineKindSchema,
    description: z
        .string()
        .describe('The line as it reads on the invoice — "Retainer — Growth+, August 2026".'),
    period: PeriodSchema.nullable().describe('The month this line is about, YYYY-MM. The retainer line is the month in advance; an ' +
        'extra or a usage line is the month in arrears. Null for a milestone.'),
    qty: z
        .number()
        .describe('How many units — articles, edits, conversations. May be fractional (6.5 hours).'),
    unit_amount: MoneySchema.describe('What one unit costs, in the invoice’s currency.'),
    amount: MoneySchema.describe('qty × unit_amount, rounded half away from zero.'),
    ref: z
        .string()
        .nullable()
        .describe('What the line came from — REQ-119, WO-2026-008, wgt_chat_9f3a. Null if nothing.'),
    approval_note: z
        .string()
        .nullable()
        .describe('Who approved the charge and when — "approved 9 Jul by Deniz Aksoy".'),
})
    .describe('One line of an invoice, snapshotted when the invoice was created.');
/**
 * A line as somebody hands it to `invoices_draft$`.
 *
 * The only hand-made lines in this system: everything else is drafted by the module
 * that owns the thing being billed (`.docs/14-billing-invoices.md` §DRY watchlist —
 * "the run assembles, it does not compute").
 */
export const DraftInvoiceLineSchema = z
    .strictObject({
    kind: InvoiceLineKindSchema,
    description: z.string().min(1).max(500).describe('The line as it should read on the invoice.'),
    qty: z.number().gt(0).describe('How many units. May be fractional, to three decimal places.'),
    unit_amount: MoneySchema.describe('What one unit costs. Negative only on a discount line, which is the one kind whose ' +
        'amount comes off rather than on.'),
    period: PeriodSchema.optional().describe('The month the line is about, if it is about one.'),
    ref: z
        .string()
        .optional()
        .describe('What it relates to — an extra, a work order, a widget. Free text.'),
    approval_note: z
        .string()
        .optional()
        .describe('Who agreed to this charge and when, in words a client will recognise.'),
})
    .describe('One line of a hand-made invoice.');
/* -------------------------------------------------------------- the invoice */
export const InvoiceSchema = z
    .object({
    ref: InvoiceRefSchema,
    client: ClientSlugSchema,
    kind: InvoiceKindSchema,
    status: InvoiceStatusSchema,
    period: PeriodSchema.nullable().describe('The retainer month this invoice carries, YYYY-MM. Null for a milestone, a deposit or ' +
        'a one-off — those are scoped by the work they belong to, not by a month.'),
    scope: z
        .string()
        .describe('What the ledger prints in its "Period / scope" column — "Aug 2026", "Website — ' +
        'milestone 2", "Mar–Apr 2026 · signup".'),
    currency: CurrencySchema.describe('The invoice’s currency. Every line matches it.'),
    subtotal: MoneySchema.describe('The lines, added up.'),
    total: MoneySchema.describe('What is owed. Equal to subtotal: there is no tax (00-index §6).'),
    paid_amount: MoneySchema.describe('The succeeded payments against it, added up. Computed on every read — settlement is ' +
        'never cached (ARCHITECTURE.md §Money pipeline).'),
    written_off: MoneySchema.describe('What the studio has written off against it, added up — money it decided not to ' +
        'collect. Never a payment, so it is not in paid_amount. Zero for almost every invoice.'),
    outstanding: MoneySchema.describe('What is still owed: total − paid_amount − written_off, computed on every read. What ' +
        'a charge, a retry and the pay button ask for. Below zero only when more arrived than ' +
        'was owed.'),
    issued_at: Rfc3339Schema.nullable().describe('When it was issued. Null while it is a draft.'),
    due_on: DateOnlySchema.nullable().describe('Issue date plus seven days (00-index §6).'),
    sent_at: Rfc3339Schema.nullable().describe('When the mail went out.'),
    paid_at: Rfc3339Schema.nullable().describe('When it became paid. The timestamp of the transition, written by settlement and by ' +
        'nothing else — not a cached "is it settled".'),
    rail_expected: PaymentRailSchema.nullable().describe('How we expect it to be paid, from the client’s rail preference. Null when they have ' +
        'not chosen one.'),
    charge_attempts: z.int().describe('How many times an off-session charge has been tried.'),
    next_retry_at: Rfc3339Schema.nullable().describe('When the next retry is due, if one is.'),
    pdf_ready: z.boolean().describe('True once the PDF has been rendered and registered.'),
    work_order_ref: z
        .string()
        .nullable()
        .describe('The project or extra this invoice is for — WO-2026-008. Null for a monthly one.'),
    milestone_seq: z
        .int()
        .nullable()
        .describe('Which milestone this invoice is for, counting from 1. Null unless it is one.'),
    notes: z.string().describe('Anything printed under the total — a mandate reference. Often "".'),
    created_at: Rfc3339Schema,
})
    .describe('One invoice, as the ledger and the studio list read it.');
export const PaymentSchema = z
    .object({
    id: z.string().describe('The payment’s id. A payment has no human reference of its own.'),
    client: ClientSlugSchema.nullable().describe('Whose money it is. Null while a Wise credit is unmatched — not knowing is the point.'),
    invoice_ref: z.string().nullable().describe('The invoice it pays. Null while it is unmatched.'),
    source: PaymentSourceSchema,
    kind: PaymentKindSchema,
    external_id: z
        .string()
        .describe('The provider’s own id — a Stripe PaymentIntent, a Wise step_id. Unique.'),
    amount: MoneySchema,
    status: PaymentStatusSchema,
    received_at: Rfc3339Schema.describe('When the money landed, as the provider reports it.'),
    matched_at: Rfc3339Schema.nullable().describe('When it was attached to an invoice.'),
    note: z.string().describe('What a person wrote about it. Empty for a machine-made one.'),
    created_at: Rfc3339Schema,
})
    .describe('One payment against one invoice.');
/**
 * One write-off against one invoice (session 44).
 *
 * Its own append-only row rather than a payment, because no money arrived: a payment
 * row would move every cash figure the studio reads. The amount is in the invoice's
 * own currency — the row has none of its own, as `invoice_line` has none — and the
 * reason is published to the client, who reads it on their own invoice.
 */
export const InvoiceWriteOffSchema = z
    .object({
    seq: z
        .int()
        .describe('Which write-off against this invoice this is, counting from 1, oldest first.'),
    amount: MoneySchema.describe('How much was written off, in the invoice’s own currency. Always more than zero.'),
    reason: z
        .string()
        .describe('Why, in the studio’s words. The client reads this on their own invoice, so it is ' +
        'written to them.'),
    created_at: Rfc3339Schema.describe('When it was written off.'),
})
    .describe('An amount the studio decided not to collect on one invoice, and why.');
export const InvoiceDetailSchema = InvoiceSchema.extend({
    lines: z.array(InvoiceLineSchema).describe('Every line, in the order it prints.'),
    payments: z.array(PaymentSchema).describe('Every payment against it, oldest first.'),
    write_offs: z
        .array(InvoiceWriteOffSchema)
        .describe('Every write-off against it, oldest first, each with its reason. Empty for almost every ' +
        'invoice. The client reads these, reasons included, on their own invoice route.'),
}).describe('One invoice with its lines, the payments that settled it and anything written off.');
/* ------------------------------------------------------------------ the run */
export const BillingRunSchema = z
    .object({
    client: ClientSlugSchema,
    period: PeriodSchema.describe('The month whose retainer the invoice carries.'),
    kind: BillingRunKindSchema,
    status: BillingRunStatusSchema,
    /*
     * A **list**, not a map from step to state.
     *
     * The column is a map — a resumed run reads `steps.invoice.state` — and the wire
     * is a list, for the reason session 12's `included_widget_qty` is: a `z.record`
     * publishes `additionalProperties` and `defineAction` refuses one in a tool
     * input, so a shape that is a record here and a map there would be two shapes.
     * The order is `BILLING_RUN_STEPS`, which is the order the run does them in.
     */
    steps: z
        .array(NamedBillingRunStepSchema)
        .describe('One entry per step the run reached, in the order it does them.'),
    invoice_ref: z
        .string()
        .nullable()
        .describe('The invoice it created, or null when there was nothing to bill.'),
    started_at: Rfc3339Schema,
    finished_at: Rfc3339Schema.nullable(),
    error: z.string().nullable().describe('Why it failed, in one sentence. Null when it did not.'),
    triggered_by: z
        .string()
        .describe('What started it — "cron", "signup", or the name of whoever asked for it.'),
})
    .describe('One client’s billing run for one period, and what each step of it did.');
/**
 * What a dry run answers with: the invoice that would be created, line by line.
 *
 * `ARCHITECTURE.md` §Catalogue: "`billing_run` takes `dry_run: true` and returns the
 * invoice it would create, line by line." It has no reference and says so — see the
 * header.
 */
export const DryRunInvoiceSchema = z
    .object({
    client: ClientSlugSchema,
    period: PeriodSchema,
    kind: BillingRunKindSchema,
    ref: z
        .null()
        .describe('Always null. A dry run mints no reference: it is rolled back, and a reference taken ' +
        'inside a transaction that is rolled back is a gap in a sequence Wise matching ' +
        'needs to be gapless.'),
    currency: CurrencySchema,
    lines: z.array(InvoiceLineSchema).describe('The lines it would write, in printing order.'),
    subtotal: MoneySchema,
    total: MoneySchema,
    /** The one number `client/billing.html` prints in a sentence: "estimated $4,417.28". */
    would_issue: z
        .boolean()
        .describe('False when there is nothing to bill — a zero-line month gets no invoice at all.'),
    /*
     * The three fields that make "what it shows is what the real run would write"
     * true rather than nearly true. Session 24 found the dry run and the real run
     * disagreeing by $41.40 on one client's August, and every part of the gap was one
     * of these two facts going unsaid.
     */
    already_issued: InvoiceRefSchema.nullable().describe('The invoice this client already has for this period, or null. When it is set, a real ' +
        'run would write nothing at all — it is a no-op on (client, period) — and `lines` and ' +
        '`total` are that invoice’s own, not a second computation of them. Re-running a ' +
        'billed period from scratch would answer with neither: the usage months it bills are ' +
        'closed and the extras it bills are marked billed, so what comes out is a month with ' +
        'no usage line and only the extras delivered since.'),
    arrears_period: PeriodSchema.describe('The month whose extras and usage these lines carry. The retainer is billed in advance ' +
        'and everything else in arrears, so a run for August bills July.'),
    arrears_in_progress: z
        .boolean()
        .describe('True when that arrears month has not ended yet. The dry run closes it anyway and ' +
        'throws the transaction away — which is what makes an estimate on the 24th include ' +
        'the days so far — but its usage will grow before the real run reaches it. False ' +
        'means the two runs read exactly the same world and the lines are exact.'),
})
    .describe('The invoice a run would create, computed and then rolled back.');
/**
 * What `GET /api/clients/me/invoices/next-estimate` answers.
 *
 * `client/billing.html` §7: "The next one is issued 1 Sep 2026, estimated $4,417.28,
 * and charged to the bank account on file." Null for a client with nothing coming —
 * `.docs/14-billing-invoices.md` §Edge cases: "Estimate route for a client with no
 * retainer → `null`, not `$0`."
 */
export const InvoiceEstimateSchema = z
    .object({
    period: PeriodSchema.describe('The month the next invoice will carry.'),
    issue_on: DateOnlySchema.describe('The billing day it will be issued on — the 1st.'),
    total: MoneySchema.describe('What it comes to, from a dry run of that period.'),
    lines: z.array(InvoiceLineSchema).describe('The lines behind that number.'),
    rail: PaymentRailSchema.nullable().describe('How it will be paid, from the rail on file.'),
})
    .describe('The next invoice, estimated. Null when there is nothing to bill.');
/* ------------------------------------------------- the review queue (session 15) */
/**
 * Why a Wise credit went to a human, as a closed list.
 *
 * `PAYMENTS.md` §Matching gives three rules and says "anything else → manual review
 * queue in admin". These are the *anything elses*, written down rather than left as
 * free text, because they are what `studio/money.html` prints as a tag on each card
 * in the queue and what the digest counts.
 *
 * Every one of them is decided by `matchCredit`, which is pure over the credit and
 * the open invoices — so a reason is a fact about two numbers rather than a sentence
 * somebody wrote.
 *
 *   `no_reference`      nothing in the transfer message normalises to an invoice ref
 *                       ("BUILDERSTOCK ODOO", "part" — `refs.normaliseReference`)
 *   `unknown_reference` it does, and no invoice has it
 *   `already_paid`      it names an invoice that is already settled
 *   `amount_mismatch`   it names an open invoice and the amount is not what is
 *                       outstanding ("$7.00 short … the sender's FX fee")
 *   `fx_converted`      Wise converted it, or the invoice is in another currency.
 *                       `PAYMENTS.md`: "FX-converted credits and partial payments
 *                       route to the review queue"
 *   `partial`           less than the one invoice it could be
 *   `surplus`           more than everything it could be, so something is left over
 *   `ambiguous_amount`  the client has more than one open invoice for that exact
 *                       amount — rule 2 says "exactly one", so this is the guess it
 *                       refuses to make
 *   `no_client`         the sender resolves to nobody and there is no reference
 */
export const MATCH_REASONS = [
    'no_reference',
    'unknown_reference',
    'already_paid',
    'amount_mismatch',
    'fx_converted',
    'partial',
    'surplus',
    'ambiguous_amount',
    'no_client',
];
export const MatchReasonSchema = z
    .enum(MATCH_REASONS)
    .describe('Why a credit could not be matched on its own: no_reference, unknown_reference, ' +
    'already_paid, amount_mismatch, fx_converted, partial, surplus, ambiguous_amount, ' +
    'no_client.');
/** An open invoice a person might allocate an unmatched credit to. */
export const MatchCandidateSchema = z
    .object({
    invoice_ref: InvoiceRefSchema,
    client: ClientSlugSchema,
    outstanding: MoneySchema.describe('Total less what has already settled it.'),
    due_on: DateOnlySchema.nullable().describe('When it was due.'),
    status: InvoiceStatusSchema,
    difference: z
        .int()
        .describe('The credit’s amount minus this invoice’s outstanding, in minor units. Zero is an ' +
        'exact match; positive is more than the invoice; negative is short of it.'),
})
    .describe('An open invoice an unmatched credit could belong to.');
/**
 * One card in `studio/money.html`'s Wise review queue.
 *
 * The read shape sessions 17 and 20 render. It carries the credit as it arrived, the
 * reasons it is here, and the candidates a person picks from — everything the page
 * prints, so the SPA joins nothing.
 */
export const UnmatchedPaymentSchema = z
    .object({
    id: z.string().describe('The payment row’s id — how payments_match names it.'),
    external_id: z.string().describe('The Wise step_id, prefixed: wise:8841b2.'),
    amount: MoneySchema.describe('What landed.'),
    received_at: Rfc3339Schema.describe('When it landed, as Wise reports it.'),
    reference: z
        .string()
        .describe('The transfer message, verbatim — "BUILDERSTOCK ODOO", "FES2026049", "part".'),
    sender: z.string().describe('The sender’s name as the bank gave it. Often empty.'),
    client: ClientSlugSchema.nullable().describe('Whose money this looks like, when the sender name or the reference said so. Null when ' +
        'nobody knows — which is most of this queue.'),
    status: PaymentStatusSchema.describe('unmatched while it waits, parked once set aside.'),
    reasons: z
        .array(MatchReasonSchema)
        .describe('Why it is here, in the order matchCredit decided them.'),
    candidates: z
        .array(MatchCandidateSchema)
        .describe('Open invoices it could be, nearest first. Empty when there are none.'),
    note: z.string().describe('What a person wrote when they parked it. Empty otherwise.'),
})
    .describe('One credit waiting for a person, and what it might belong to.');
/**
 * One allocation of an unmatched credit onto one invoice.
 *
 * A list of these is how `payments_match` settles two invoices from one transfer —
 * `studio/money.html`: "Split across FES-2026-039 and -049".
 */
export const PaymentAllocationSchema = z
    .strictObject({
    invoice: InvoiceRefSchema.describe('The invoice this part of the credit pays.'),
    amount: MoneySchema.describe('How much of the credit goes to it, in the credit’s currency.'),
})
    .describe('Part of a credit, allocated to one invoice.');
/* ---------------------------------------------- the payment method (session 15) */
/**
 * Whether the method on file can be charged.
 *
 * `PAYMENTS.md` §Flow: "Checkout defaults to instant with microdeposit fallback",
 * and microdeposits "take 1–2 business days". `pending` is that window, and it is a
 * state rather than a boolean because a charge in it is deferred rather than failed.
 */
export const PAYMENT_METHOD_VERIFICATIONS = ['verified', 'pending', 'unverified'];
export const PaymentMethodVerificationSchema = z
    .enum(PAYMENT_METHOD_VERIFICATIONS)
    .describe('verified — usable now. pending — an ACH account in microdeposit verification, which takes ' +
    'one to two business days and cannot be charged yet. unverified — nothing usable.');
/**
 * The card or bank account on file, as the client’s own settings page reads it.
 *
 * `mockups/client/settings.html` prints a masked tail and the rail, never the
 * gateway’s id: `security/mask.ts` is what produces `••••4412`, and
 * `client.payment_method_id` never leaves the API.
 */
export const PaymentMethodSchema = z
    .object({
    rail: PaymentRailSchema.nullable().describe('How this client pays. wise means we never debit them: they push the money and quote the ' +
        'invoice reference.'),
    display: z
        .string()
        .nullable()
        .describe('The masked tail, as it should print — "••••4412". Null when nothing is on file.'),
    bank_name: z
        .string()
        .nullable()
        .describe('The bank or the card brand, when the gateway told us one.'),
    verification: PaymentMethodVerificationSchema,
    verified_at: Rfc3339Schema.nullable().describe('When it became usable.'),
    retries_allowed: z
        .int()
        .describe('How many further attempts a failed charge on this rail gets, from the retry schedule ' +
        'in PAYMENTS.md. Zero when nothing is on file.'),
})
    .describe('The payment method on file, masked.');
/** Where a Checkout Session sends the browser. The one shape both pay routes answer. */
export const CheckoutSessionSchema = z
    .object({
    url: z.string().describe('The hosted Checkout page. Send the browser here.'),
    external_id: z.string().describe('The gateway’s id for the session, for the log.'),
    amount: MoneySchema.nullable().describe('What will be taken. Null in setup mode, where nothing is charged.'),
})
    .describe('A hosted payment page, ready for the browser.');
/* ------------------------------------------------------ the ageing (session 15) */
/**
 * `studio/money.html`’s four ageing bands, in its order: `1–30 d`, `31–60 d`,
 * `61–90 d`, `90+ d`.
 *
 * `upTo` is the last day of the band and `null` is the open end. The bands are
 * inclusive of both ends and are counted in whole days past the **due date**, so an
 * invoice due yesterday is one day past due and is in the first band.
 */
export const PAST_DUE_BUCKETS = [
    { label: '1–30 d', from: 1, upTo: 30 },
    { label: '31–60 d', from: 31, upTo: 60 },
    { label: '61–90 d', from: 61, upTo: 90 },
    { label: '90+ d', from: 91, upTo: null },
];
/**
 * Which band a number of days past due falls in. One rule, and this is it.
 *
 * Null for anything that is not past due — and for anything that is not a number of
 * days at all. The second is not hypothetical: a caller computing the days from a
 * `due_on` nobody set would hand this `NaN`, and a band chosen for `NaN` would put an
 * invoice into `1–30 d` on the strength of an arithmetic accident.
 */
export function pastDueBucketOf(daysPastDue) {
    if (!Number.isFinite(daysPastDue) || daysPastDue < 1)
        return null;
    const bucket = PAST_DUE_BUCKETS.find((band) => daysPastDue >= band.from && (band.upTo === null || daysPastDue <= band.upTo));
    return bucket?.label ?? null;
}
//# sourceMappingURL=billing.js.map